import { useElasticData, useExtraData } from "../DataContext.js";
import {getFormattedDate, getFormattedPrice, BOOK_KEYSTAGES, BOOK_SUITABILITIES} from '../helpers.js';
import BookCoverSize from '../BookCoverSize.js';

const bicCodeJsonTemplate = (code) => {
    return `{"bicCodes":{"value":"${code}"}}`
}

const themaJsonTemplate = (code) => {
    return `{"thema":{"code":"${code}"}}`
}

const ukslcSubjectJsonTemplate = (code) => {
    return `{"ukslc":{"subject":{"value":"${code}"}}}`
}

const ukslcAgeJsonTemplate = (code) => {
    return `{"ukslc":{"age":{"value":"${code}"}}}`
}

const ukslcTreatmentJsonTemplate = (code) => {
    return `{"ukslc":{"treatment":{"value":"${code}"}}}`
}

const ukslcAudienceJsonTemplate = (code) => {
    return `{"ukslc":{"audience":{"value":"${code}"}}}`
}

const ukslcBootypeJsonTemplate = (code) => {
    return `{"ukslc":{"booktype":{"value":"${code}"}}}`
}

const ukslcBroadSubjectJsonTemplate = (code) => {
    return `{"ukslc":{"broad_subject":{"value":"${code}"}}}`
}

const ukslcGenreJsonTemplate = (code) => {
    return `{"ukslc":{"genre":{"value":"${code}"}}}`
}

const ukslcInterestJsonTemplate = (code) => {
    return `{"ukslc":{"interest":{"value":"${code}"}}}`
}

const fullKeystageText = (ks => {
    return ks?.map(k => (BOOK_KEYSTAGES[k]))?.sort((a, b) => a > b).join(" / ")
})
const shortKeystageText = (ks => {
    return ks?.map(k => (BOOK_KEYSTAGES[k]))?.map(e => e?.replace("Key Stage ",""))?.sort((a, b) => a > b).join(", ");
})
const suitabilitiesText = (suits => {
    return suits?.map(s => (BOOK_SUITABILITIES[s]))?.map(e => e?.replace("Year ",""))?.sort((a, b) => a > b).join(", ");
})

const buildLinks = (codes, codesTextMap, queryJsonTemplate) => {
    let linksHtml = "";
    
    if(Array.isArray(codes) && codes.length > 0) {        
        codes.forEach((code, index) => {
            let codestring = code
            if (typeof code === 'object' && !Array.isArray(code) && code !== null) {
                codestring = code.value;
            }

            let codeQuery = btoa(queryJsonTemplate(codestring));
            let codeText = codesTextMap[codestring]?.description ? codesTextMap[codestring].description : codesTextMap[codestring];
            let comma = index < codes.length - 1 ? "," : "";
        
            if(codeText){
                linksHtml += `<p class="needMore"><a href="/search-results/${codeQuery}">${codestring}</a><span class="searchToolTip">${codeText}</span>${comma}</p>`;
            } else if(codestring){
                linksHtml += `<p>${codestring}${comma}</p>`;
            }
        });

    } else if(!Array.isArray(codes) && typeof codes === "string"){
        let codeQuery = btoa(queryJsonTemplate(codes));
        let codeText = codesTextMap[codes];

        if(codeText){
            linksHtml += `<p class="needMore"><a href="/search-results/${codeQuery}">${codes}</a><span class="searchToolTip">${codeText}</span></p>`;
        } else if(codes) {
            linksHtml += `<p>${codes}</p>`;
        }
    } else if(!Array.isArray(codes) && typeof codes === "object"){
        let codeQuery = btoa(queryJsonTemplate(codes.value));
        let codeText = codesTextMap[codes.value];

        if(codeText){
            linksHtml += `<p class="needMore"><a href="/search-results/${codeQuery}">${codes.value}</a><span class="searchToolTip">${codeText}</span></p>`;
        } else if(codes.value) {
            linksHtml += `<p>${codes.value}</p>`;
        }
    } 

    if(linksHtml) {
        return `<div class="linksList">${linksHtml}</div>`;
    }
    return
}

const DetailsPanel = ({tabIndex, sel}) => {

    let { productData, bindings, themaCodes, ukslcCodes, bicCodes } = useElasticData();
        
    let { extraData, _source, isGuestUser, isLibraryUser, isSchoolsUser } = useExtraData();

    const authorDetails = () => {
        let details = [];

        if (_source.author_is_a_list === true) {
            details.push("A-list children's");
        }
        if (_source.author_is_b_list === true) {
            details.push("B-list children's");
        }
        if (_source.author_is_a_list_adult === true) {
            details.push("A-list adult");
        } 
        if (_source.author_is_b_list_adult === true) {
            details.push("B-list adult");
        }
        if (_source.author_is_scottish === true) {
            details.push("Scottish");
        }
        if (_source.author_is_welsh === true) {
            details.push("Welsh");
        }
        if (_source.author_is_bame === true) {
            details.push("BAME");
        }
        if (_source.author_is_irish === true) {
            details.push("Irish");
        }

        if (details.length) {
            return details.join(', ');
        } else {
            return false;
        }
    }

    const name = `tab-${tabIndex}`;
    // Details
    let _details = [];

    if(isGuestUser || isSchoolsUser){
        _details[0] = {
            "heading" : "",
            "rows" : {}
        };

        if(_source.category){
            _details[0].rows["Category"] = _source.category;
        }
        if(_source.peters_subject){
            _details[0].rows["Subject"] = _source.peters_subject;
        }

        if(extraData && !!(extraData.customerDewey)){
            _details[0].rows["Customer dewey"] = extraData.customerDewey
        }

        if(_source.language && _source.language?.length > 0){
            _details[0].rows[`Language${_source.language?.length > 1 ? "s" : ""}`] = _source.language?.map(l => l.title).join("/");
        }

        if(_source.dewey){
            _details[0].rows["Dewey"] = _source.dewey;
        }

        if(fullKeystageText(_source.keystage)){
            _details[0].rows["Key stage"] = fullKeystageText(_source.keystage);
        }

        if(isGuestUser){
            _details[0].rows["Reading age"] = `<div class="loginRequired"><a class="login">Log in</a> to view reading age.</div>`;

        } else if(!isGuestUser && (_source.lower_reading_age || _source.upper_reading_age)){
            let readingAgeRange;
            
            if(_source.lower_reading_age && _source.upper_reading_age){
                readingAgeRange = `${_source.lower_reading_age} - ${_source.upper_reading_age}`
            }
            if(_source.lower_reading_age && !_source.upper_reading_age){
                readingAgeRange = `${_source.lower_reading_age} and above`
            }
            if(!_source.lower_reading_age && _source.upper_reading_age){
                readingAgeRange = `up to ${_source.upper_reading_age}`
            }

            _details[0].rows["Reading age"] = readingAgeRange;
        } 

        if(isGuestUser){
            _details[0].rows["Interest Level"] = `<div class="loginRequired"><a class="login">Log in</a> to view interest age.</div>`;

        } else if(!isGuestUser && (_source.lower_interest_age || _source.upper_interest_age)){
            let interestAgeRange;
            
            if(_source.lower_interest_age && _source.upper_interest_age){
                interestAgeRange = `${_source.lower_interest_age} - ${_source.upper_interest_age}`
            }
            if(_source.lower_interest_age && !_source.upper_interest_age){
                interestAgeRange = `${_source.lower_interest_age} and above`
            }
            if(!_source.lower_interest_age && _source.upper_interest_age){
                interestAgeRange = `up to ${_source.upper_interest_age}`
            }

            _details[0].rows["Interest Level"] = interestAgeRange;
        }

        if(_source.primary_illustrator_nat){
            _details[0].rows["Illustrator"] = `<a href='/search-results?searchTerm=${_source.primary_illustrator_nat}&searchType=ill'>${_source.primary_illustrator_nat}</a>`;
        }

        if(_source.illustrations){
            _details[0].rows["Illustrations"] = _source.illustrations;
        }

        if(_source.series_display){
            _details[0].rows["Series"] = `<a href='/search-results?searchTerm="${_source.series_display}"&searchType=series'>${_source.series_display}</a>`;
        }

        if(_source.volume){
            _details[0].rows["Series volume"] = _source.volume;
        }

        if(_source.publisher.title && _source.publisher.title != "UNKNOWN"){
            _details[0].rows["Publisher"] = `<a href='/search-results?searchTerm=${encodeURIComponent(_source.publisher.title)}&searchType=pub'>${_source.publisher.title}</a>`;
        }

        if (_source.imprint && _source.imprint.title != "" && _source.imprint.title != _source.publisher.title && _source.imprint.title != "UNKNOWN") {
            _details[0].rows["Imprint"] = `<a href='/search-results?searchTerm=${encodeURIComponent(_source.imprint.title)}&searchType=pub'>${_source.imprint.title}</a>`;
        }

        if(_source.publish_date){
            _details[0].rows["Pub. date"] = getFormattedDate(_source.publish_date);
        }

        if(_source.pub_history){
            _details[0].rows["Pub. history"] = _source.pub_history;
        }

        if(_source.edition){
            _details[0].rows["Edition"] = _source.edition;
        }

        if(_source.id || _source.isbn){
            let isbn = _source.id ? _source.id : _source.isbn;
            _details[0].rows["ISBN"] = isbn;
        }

        if(_source.bic_code){
            _details[0].rows["BIC code"] = `<a href='/search-results?bic=${_source.bic_code.value}'>${_source.bic_code.value}</a>`;
        }

        _details[1] = {
            "heading" : "",
            "rows" : {}
        };

        if(_source.binding){
            _details[1].rows["Binding"] = bindings[_source.binding];
        }

        if(_source.pages){
            _details[1].rows["Pages"] = _source.pages;
        }

        if(_source.width){
            _details[1].rows["Width"] = `${_source.width}mm`;
        }

        if(_source.height){
            _details[1].rows["Height"] = `${_source.height}mm`;
        }

        if(_source.weight > 0){
            _details[1].rows["Weight"] = `${_source.weight}g`;
        }

        _details[1].rows["Index"] = `${_source.index  ? "Yes" : "No"}`;
        _details[1].rows["Contents page"] = `${_source.contents_page ? "Yes" : "No"}`;
        _details[1].rows["Glossary"] = `${_source.glossary ? "Yes" : "No"}`;
        _details[1].rows["CE Mark"] = `${_source.ce_mark ? "Yes" : "No"}`;


        if(parseFloat(productData.peVatSplit) && parseFloat(productData.peVatCode)){
            let totalVat = parseFloat(productData.peVatSplit) * parseFloat(productData.peVatCode);
            _details[1].rows["VAT-able amount"] = `${getFormattedPrice(totalVat)}`;
        }

        if(authorDetails()){
            _details[1].rows["Author attributes"] = authorDetails();
        }
        
        if(_source.width > 0 && _source.height > 0){
            _details[1].rows["Product dimensions"] = `<div></div>`;
        }
    }

    if(isLibraryUser()){
        // Physical characteristics column
        _details[0] = {
            "heading" : "Physical characteristics",
            "rows" : {}
        };

        if(_source.binding){
            _details[0].rows["Binding"] = bindings[_source.binding];
        }

        if(_source.width){
            _details[0].rows["Width"] = `${_source.width}mm`;
        }

        if(_source.height){
            _details[0].rows["Height"] = `${_source.height}mm`;
        }

        if(_source.weight > 0){
            _details[0].rows["Weight"] = `${_source.weight}g`;
        }

        if(_source.illustrations){
            _details[0].rows["Illustrations"] = _source.illustrations;
        }

        _details[0].rows["Index"] = `${_source.index ? "Yes" : "No"}`;
        _details[0].rows["Contents page"] = `${_source.contents_page ? "Yes" : "No"}`;
        _details[0].rows["Glossary"] = `${_source.glossary ? "Yes" : "No"}`;
        _details[0].rows["CE Mark"] = `${_source.ce_mark ? "Yes" : "No"}`;

        if(authorDetails()){
            _details[0].rows["Author attributes"] = authorDetails();
        }

        if(_source.format_issues){
            _details[0].rows["Format issues"] = _source.format_issues.join(' | ')
        }
        // Appeal column
        _details[1] = {
            "heading" : "Appeal",
            "rows" : {

            }
        };

        if(shortKeystageText(_source.keystage)){
            _details[1].rows["Key stage"] = shortKeystageText(_source.keystage);
        }

        if(suitabilitiesText(_source.year_group_suitability)){
            _details[1].rows["Year groups"] = suitabilitiesText(_source.year_group_suitability);
        }

        _details[1].rows["Hi Lo?"] = `${_source.hi_lo ? "Yes" : "No"}`;
        _details[1].rows["Leisure NF"] = `${_source.leisure_nf ? "Yes" : "No"}`;
        _details[1].rows["Curriculum NF"] = `${_source.curriculum_nf ? "Yes" : "No"}`;
        // Bibliographic column
        _details[2] = {
            "heading" : "Bibliographic",
            "rows" : {}
        }

        if(_source.thema_codes){
            const themaLinksHtml = buildLinks(_source.thema_codes, themaCodes, themaJsonTemplate);
            if(themaLinksHtml) {
                _details[2].rows["Thema"] = buildLinks(_source.thema_codes, themaCodes, themaJsonTemplate);
            }
        }

        if(_source.bic_code){
            const biLinksHtml = buildLinks(_source.bic_code, bicCodes, bicCodeJsonTemplate);
            if(biLinksHtml) {
                _details[2].rows["BIC code"] = buildLinks(_source.bic_code, bicCodes, bicCodeJsonTemplate);
            }
        }

        if(_source.ukslc_subject){
            const subjectLinksHtml = buildLinks(_source.ukslc_subject, ukslcCodes, ukslcSubjectJsonTemplate);
            if(subjectLinksHtml) {
                _details[2].rows["UKSLC subject"] = buildLinks(_source.ukslc_subject, ukslcCodes, ukslcSubjectJsonTemplate);
            }
        }

        if(_source.ukslc_age){
            const ageLinksHtml = buildLinks(_source.ukslc_age, ukslcCodes, ukslcAgeJsonTemplate);
            if(ageLinksHtml) {
                _details[2].rows["UKSLC age"] = buildLinks(_source.ukslc_age, ukslcCodes, ukslcAgeJsonTemplate);
            }
        }

        if(_source.ukslc_treatment){
            const treatmentLinksHtml = buildLinks(_source.ukslc_treatment, ukslcCodes, ukslcTreatmentJsonTemplate);
            if(treatmentLinksHtml) {
                _details[2].rows["UKSLC treatment"] = buildLinks(_source.ukslc_treatment, ukslcCodes, ukslcTreatmentJsonTemplate);
            }
        }

        if(_source.ukslc_audience){
            const audienceLinksHtml = buildLinks(_source.ukslc_audience, ukslcCodes, ukslcAudienceJsonTemplate);
            if(audienceLinksHtml) {
                _details[2].rows["UKSLC audience"] = buildLinks(_source.ukslc_audience, ukslcCodes, ukslcAudienceJsonTemplate);
            }
        }

        if(_source.ukslc_booktype){
            const booktypeLinksHtml = buildLinks(_source.ukslc_booktype, ukslcCodes, ukslcBootypeJsonTemplate);
            if(booktypeLinksHtml) {
                _details[2].rows["UKSLC booktype"] = buildLinks(_source.ukslc_booktype, ukslcCodes, ukslcBootypeJsonTemplate);
            }
        }

        if(_source.ukslc_broad_subject){
            const broadLinksHtml = buildLinks(_source.ukslc_broad_subject, ukslcCodes, ukslcBroadSubjectJsonTemplate);
            if(broadLinksHtml) {
                _details[2].rows["UKSLC broad subject"] = buildLinks(_source.ukslc_broad_subject, ukslcCodes, ukslcBroadSubjectJsonTemplate);
            }
        }

        if(_source.ukslc_genre){
            const genreLinksHtml = buildLinks(_source.ukslc_genre, ukslcCodes, ukslcGenreJsonTemplate);
            if(genreLinksHtml) {
                _details[2].rows["UKSLC genre"] = buildLinks(_source.ukslc_genre, ukslcCodes, ukslcGenreJsonTemplate);
            }
        }

        if(_source.ukslc_interest){
            const interestLinksHtml = buildLinks(_source.ukslc_interest, ukslcCodes, ukslcInterestJsonTemplate);
            if(interestLinksHtml) {
                _details[2].rows["UKSLC interest"] = interestLinksHtml;
            }
        }

        if (_source.library_of_congress_class) {
            _details[2].rows["Library of Congress class"] = _source.library_of_congress_class;
        }

        if (_source.library_of_congress_headings) {
            // regex here to add spaces to commas missing them
            _details[2].rows["Library of Congress headings"] = _source.library_of_congress_headings.join(", ");
        }

        if(_source.pub_history){
            _details[2].rows["Publish history"] = _source.pub_history;
        }
    }

    const isObjectEmpty = (objectName) => {
        return JSON.stringify(objectName) === "{}";
    };
    // Check for empty columns and strip from array
    _details.forEach((sec, i) => {
        if(isObjectEmpty(sec.rows)) {
            _details.splice(i, 1);
        }
    })
    // Set column size on number of groups in array
    let _columnsClass = "";

    if(_details.length == 2) {
        _columnsClass = "half column"
    } else if(_details.length == 3) {
        _columnsClass = "third column"
    } else if(_details.length == 4) {
        _columnsClass = "quarter column"
    }

    const detailsHeadings = _details.map(x => x.heading);
    const detailsRows = Object.values(_details.map(x => x.rows));

    const panelId = "tab-" + tabIndex
    const panelClass = "tabContent " + ((name == sel) ? "current" : "")

    return(
        <div id={panelId} key={tabIndex} className={panelClass}>
            <div className="columns wrap">{
                detailsRows.map((row, i) => {
                    let rowLabels = Object.keys(row);
                    let rowValues = Object.values(row);
                    let heading = (detailsHeadings[i]) ? detailsHeadings[i] : "";

                    return(<div key={i} className={_columnsClass}>
                        <h4>{heading}</h4>
                        <div className="columns wrap">
                            {rowLabels.map((label, j) => (<React.Fragment key={label}>
                                <div className="half column"><strong>{label}</strong></div>
                                {(label == "Product dimensions") ?
                                    (<div className="half column"><div id="productDimensions"><BookCoverSize width={_source.width} height={_source.height} /></div></div>)
                                    : (<div className="half column" dangerouslySetInnerHTML={{__html: rowValues[j]}}/>)
                                }
                            </React.Fragment>))}
                        </div>
                    </div>)
                })}
            </div>
        </div>
    )
}

export default DetailsPanel;